<template>
  <div class="assistant-card stock-card">
    <template v-if="countries.length">
      <p class="stock-card__section">Por país</p>
      <div class="stock-card__scroll">
        <table>
          <thead>
            <tr>
              <th>Pieza</th>
              <th
                v-for="country in countries"
                :key="country.store"
                :title="country.name"
              >
                {{ countryLabel(country.name) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.code">
              <td>
                <div class="stock-piece">
                  <strong>
                    <NkrCodeLink v-if="row.found" :code="row.nkrCode" />
                    <template v-else>{{ row.code }}</template>
                  </strong>
                  <span :class="{ 'is-out': !row.found && !row.title }">
                    {{ pieceDetail(row) }}
                  </span>
                </div>
              </td>
              <td
                v-for="country in countries"
                :key="country.store"
                :class="countryClass(row, country.store)"
                :title="
                  countryOf(row, country.store) &&
                  !countryOf(row, country.store).enabled
                    ? 'No está en la tienda de este país'
                    : ''
                "
              >
                {{ countryValue(row, country.store) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-if="stores.length">
      <p class="stock-card__section">Tiendas de este país</p>
      <div class="stock-card__scroll">
        <table>
          <thead>
            <tr>
              <th>Pieza</th>
              <th v-for="store in stores" :key="store.code" :title="store.name">
                {{ store.code }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.code">
              <td>
                <div class="stock-piece">
                  <strong>
                    <NkrCodeLink v-if="row.found" :code="row.nkrCode" />
                    <template v-else>{{ row.code }}</template>
                  </strong>
                  <span :class="{ 'is-out': !row.found && !row.title }">
                    {{ pieceDetail(row) }}
                  </span>
                </div>
              </td>
              <td
                v-for="store in stores"
                :key="store.code"
                :title="warehousesTitle(row, store.code)"
                :class="{ 'is-out': row.found && !totalIn(row, store.code) }"
              >
                {{ row.found ? totalIn(row, store.code) : "—" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="stock-card__hint">
        Pasa el cursor sobre un número para ver las bodegas. El total por país
        es del último sync de cada país.
      </p>
    </template>
  </div>
</template>

<script>
import NkrCodeLink from "./NkrCodeLink.vue";

// Existencias de check_stock: una fila por pieza; columnas por país y por
// tienda (departamento) del país actual
export default {
  name: "StockCard",
  components: { NkrCodeLink },
  props: {
    rows: { type: Array, required: true }
  },
  computed: {
    countries() {
      return this.uniqueBy("countries", "store");
    },
    // En el orden de la primera pieza (la tienda del vendedor primero)
    stores() {
      return this.uniqueBy("stores", "code");
    }
  },
  methods: {
    // Debajo del código: el título, o que no está en este país
    pieceDetail(row) {
      if (row.found) return row.title;
      return row.title
        ? `${row.title} (no está en este país)`
        : "No encontrada";
    },
    uniqueBy(list, key) {
      const seen = new Map();
      this.rows.forEach(row =>
        (row[list] || []).forEach(entry => {
          if (!seen.has(entry[key])) seen.set(entry[key], entry);
        })
      );
      return Array.from(seen.values());
    },
    // "Costa Rica - NKR" -> "Costa Rica"
    countryLabel(name) {
      return String(name).replace(/\s*-\s*NKR$/i, "");
    },
    countryOf(row, store) {
      return (row.countries || []).find(country => country.store === store);
    },
    countryValue(row, store) {
      const country = this.countryOf(row, store);
      return country && country.enabled ? country.stock : "—";
    },
    countryClass(row, store) {
      const country = this.countryOf(row, store);
      return {
        "is-out": country && country.enabled && !country.stock,
        "is-missing": !country || !country.enabled
      };
    },
    storeOf(row, code) {
      return (row.stores || []).find(store => store.code === code);
    },
    totalIn(row, code) {
      const store = this.storeOf(row, code);
      return store ? store.total : 0;
    },
    warehousesTitle(row, code) {
      const store = this.storeOf(row, code);
      if (!store || !store.warehouses.length) return "";
      return store.warehouses
        .map(warehouse => `${warehouse.code}: ${warehouse.stock}`)
        .join("\n");
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./card.scss";

.stock-card__section {
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  & ~ .stock-card__section {
    margin-top: 10px;
  }
}
// Con muchas tiendas la tabla se desplaza de lado; el alto es el suyo
.stock-card__scroll {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
th,
td {
  padding: 4px 6px;
  border-bottom: 1px solid #eef0f3;
  text-align: right;
  vertical-align: top;
  white-space: nowrap;
}
th:first-child,
td:first-child {
  text-align: left;
  white-space: normal;
  min-width: 140px;
}
.stock-piece {
  display: flex;
  flex-direction: column;
  span {
    color: #6b7280;
    font-size: 11px;
  }
}
.is-out {
  color: #b91c1c;
}
.is-missing {
  color: #9ca3af;
}
.stock-card__hint {
  margin-top: 6px;
  font-size: 11px;
  color: #9ca3af;
}
</style>
